import type {CheckoutAttempt} from './payment';

// The confirmation token behind an attempt expires 12 hours after it's made, so past that a resend
// can't finish anything it started. Within the 24 hours Stripe remembers an idempotency key, even for
// a key that went without a card for up to 12 hours before it got one.
export const ATTEMPT_LIFETIME_MS = 12 * 60 * 60 * 1000;

interface StoredAttempt {
  attempt: CheckoutAttempt;
  /** When the attempt's token was made, or its key while it has none, in epoch milliseconds. */
  createdAt: number;
}

export interface CheckoutAttemptStore {
  get: (groupKey: string) => CheckoutAttempt | undefined;
  set: (groupKey: string, attempt: CheckoutAttempt) => void;
  delete: (groupKey: string) => void;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStoredAttempt = (value: unknown): value is StoredAttempt =>
  isRecord(value) &&
  typeof value.createdAt === 'number' &&
  isRecord(value.attempt) &&
  // A checkout prepaid credit covers in full goes without a card.
  (value.attempt.confirmationTokenId === undefined ||
    typeof value.attempt.confirmationTokenId === 'string') &&
  typeof value.attempt.checkoutKey === 'string' &&
  typeof value.attempt.mayHaveCharged === 'boolean' &&
  // Attempts stored before the note existed have none, and they still have to resume.
  (value.attempt.note === undefined || typeof value.attempt.note === 'string') &&
  (value.attempt.cardNeededFor === undefined || typeof value.attempt.cardNeededFor === 'string');

// What each scope's attempts were last known to be, shared by every store on the page so a modal or
// page that remounts still finds an attempt storage couldn't hold.
const inMemory = new Map<string | null, Record<string, StoredAttempt>>();
// Scopes whose last write storage refused. Storage still holds what was there before, so reading it
// back would drop the attempt that write was keeping; memory stays the record until a write lands.
const unsaved = new Set<string>();

/**
 * The cart's checkout attempts, kept beside the stored cart so they outlive the page. The cart
 * itself survives a reload, and a fresh key for a charge that may already exist is a second charge
 * for the same scenes. Read on every call, so a submit in another tab sees this one's attempts.
 *
 * `scopeKey` is the cart's own storage key; null, or storage that can't be used, keeps the attempts
 * in memory until the page reloads.
 */
export function checkoutAttemptStore(
  scopeKey: string | null,
  now: () => number = Date.now
): CheckoutAttemptStore {
  const storageKey = scopeKey && `${scopeKey}.checkouts`;

  const read = (): Record<string, StoredAttempt> => {
    if (storageKey && !unsaved.has(storageKey)) {
      try {
        const raw = localStorage.getItem(storageKey);
        const parsed: unknown = raw ? JSON.parse(raw) : {};
        const entries = Object.fromEntries(
          Object.entries(isRecord(parsed) ? parsed : {}).filter(
            ([, entry]) => isStoredAttempt(entry) && now() - entry.createdAt < ATTEMPT_LIFETIME_MS
          )
        ) as Record<string, StoredAttempt>;
        inMemory.set(storageKey, entries);
      } catch {
        // Unreadable storage leaves what this page last knew.
      }
    }
    return inMemory.get(storageKey) ?? {};
  };

  const write = (next: Record<string, StoredAttempt>) => {
    inMemory.set(storageKey, next);
    if (!storageKey) {
      return;
    }
    try {
      if (Object.keys(next).length === 0) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify(next));
      }
      unsaved.delete(storageKey);
    } catch {
      unsaved.add(storageKey);
    }
  };

  return {
    // An attempt found here at submit time either outlived an answer that tied it to a charge, or
    // never got its answer back to this page: a reload mid-request, or another tab's submit. Either
    // way it's held as one that may have charged.
    get: (groupKey) => {
      const entry = read()[groupKey];
      return entry && {...entry.attempt, mayHaveCharged: true};
    },
    set: (groupKey, attempt) => {
      const current = read();
      const existing = current[groupKey];
      const createdAt =
        existing?.attempt.checkoutKey === attempt.checkoutKey &&
        existing.attempt.confirmationTokenId === attempt.confirmationTokenId
          ? existing.createdAt
          : now();
      write({...current, [groupKey]: {attempt, createdAt}});
    },
    delete: (groupKey) => {
      const {[groupKey]: _removed, ...rest} = read();
      write(rest);
    },
  };
}
